import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiJson, ApiError } from '@/lib/apiClient';
import type { TripDetail, TripListResponse, TripPositionsResponse } from './types';

/**
 * The three trips endpoints, consumed exactly as PLAN §2.5 defines them (S1).
 *
 * The two trip-keyed routes are keyed by TRIP id, not vehicle id, so the tenant
 * predicate lives server-side in the same query as the id lookup. From here a
 * foreign trip and a nonexistent one are indistinguishable by design — both are
 * a byte-identical 404 — and both render as "not found", never as an error the
 * user could read as "it exists but you are blocked". Confirming existence is
 * precisely the leak `CLAUDE.md` forbids.
 */

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 400);
}

/**
 * The PREFIX every trips query key starts with, exported so a mutation elsewhere
 * can invalidate "all trips data" without listing the four key builders below
 * (TanStack matches keys by prefix).
 *
 * Slice 24 is its only consumer: a place RENAME propagates through history — the
 * trips read joins `places` and renders the place's CURRENT name — so a cached
 * trips page is stale by one string the moment a place is renamed, and
 * "apply to past trips" fills endpoints that previously showed a coordinate. It
 * is a CACHE key, not a write path: nothing that uses it modifies a trip row.
 */
export const tripsQueryKeyRoot = ['trips'] as const;

export const vehicleTripsQueryKey = (vehicleId: string, from: string, to: string) =>
  ['trips', 'byVehicle', vehicleId, from, to] as const;

export function useVehicleTrips(
  vehicleId: string,
  range: { from: string; to: string },
  opts: { enabled?: boolean } = {},
): UseQueryResult<TripListResponse> {
  const { from, to } = range;
  return useQuery({
    queryKey: vehicleTripsQueryKey(vehicleId, from, to),
    enabled: opts.enabled ?? Boolean(vehicleId),
    queryFn: ({ signal }) =>
      apiJson<TripListResponse>(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/trips?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        { signal },
      ),
    // A 404/400 is a definitive answer about a vehicle, not a transient failure.
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

/**
 * The FLEET-scoped trip list. `GET /api/trips?from&to[&vehicleId]` (PLAN §2.6).
 *
 * This is the top-level Trips page's data (canvas 1e). It is the SAME wire shape
 * as the per-vehicle list — `{ trips: TripListItem[], serverTime }` — deliberately
 * NOT widened with a plate: a trip is immutable history and a plate is mutable
 * vehicle identity, so the plate is joined client-side against `useVehicles`
 * rather than copied onto every trip row (PLAN §2.6, and `types.ts` S1).
 *
 * `from`/`to` are required — an unbounded fleet-wide scan is the thing §6 names.
 * Omitting `vehicleId` means every vehicle of the caller's tenant; a `vehicleId`
 * belonging to another tenant returns an empty list, not a 404, because the route
 * is a collection and must not become a probe (server-side, PLAN §2.6).
 */
export const fleetTripsQueryKey = (from: string, to: string, vehicleId: string | null) =>
  ['trips', 'fleet', from, to, vehicleId ?? 'all'] as const;

export function useFleetTrips(
  range: { from: string; to: string },
  vehicleId: string | null = null,
): UseQueryResult<TripListResponse> {
  const { from, to } = range;
  return useQuery({
    queryKey: fleetTripsQueryKey(from, to, vehicleId),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ from, to });
      if (vehicleId) params.set('vehicleId', vehicleId);
      return apiJson<TripListResponse>(`/api/trips?${params.toString()}`, { signal });
    },
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

export const tripQueryKey = (tripId: string) => ['trips', 'detail', tripId] as const;

export function useTrip(tripId: string): UseQueryResult<TripDetail> {
  return useQuery({
    queryKey: tripQueryKey(tripId),
    enabled: Boolean(tripId),
    queryFn: ({ signal }) =>
      apiJson<TripDetail>(`/api/trips/${encodeURIComponent(tripId)}`, { signal }),
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

export const tripPositionsQueryKey = (tripId: string) => ['trips', 'positions', tripId] as const;

/**
 * The replay track. `retained: false` comes back as a 200 with an empty
 * `positions` array — the trip happened, only its detail has aged out — so this
 * hook must NOT be given a transform that treats an empty array as failure. The
 * caller distinguishes the two states from `retained`, never from `length`.
 *
 * A completed trip's positions never change, so this is cached hard: refetching
 * a 400-point track on a Mogadishu mobile connection every time the tab regains
 * focus costs the user money for a byte-identical answer.
 */
export function useTripPositions(tripId: string): UseQueryResult<TripPositionsResponse> {
  return useQuery({
    queryKey: tripPositionsQueryKey(tripId),
    enabled: Boolean(tripId),
    queryFn: ({ signal }) =>
      apiJson<TripPositionsResponse>(`/api/trips/${encodeURIComponent(tripId)}/positions`, {
        signal,
      }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}
